import React from "react";
import { CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import {
  useGetDoctorsQuery,
  useGetGroupsQuery,
} from "@/features/members-groups/infrastructure/api/membersGroupsApi";
import { useTranslation } from "react-i18next";

interface RecommendedDoctorsSectionProps {
  patientId: string;
  assessmentId?: string;
  selectedDoctor?: string; // ✅ single चयन
  onDoctorChange: (doctorId: string) => void;
}

export const RecommendedDoctorsSection: React.FC<
  RecommendedDoctorsSectionProps
> = ({ patientId, selectedDoctor, onDoctorChange }) => {
  const { t } = useTranslation("patients");
  const { data: groups = [], isLoading: isGroupsLoading } = useGetGroupsQuery(
    undefined,
    { skip: !patientId },
  );
  const groupIds = groups.map((group) => group.id);
  const { data: doctors = [], isLoading: isDoctorsLoading } = useGetDoctorsQuery(
    { group_ids: groupIds },
    { skip: groupIds.length === 0 },
  );
  const isLoading = isGroupsLoading || isDoctorsLoading;

  return (
    <div className="border-gray-200">
      <div className="pb-3">
        <CardTitle className="text-base font-semibold text-gray-900">
          {t("clinicalSections.recommendedDoctors.title")}
        </CardTitle>
        <p className="text-sm text-gray-500">
          {t("clinicalSections.recommendedDoctors.subtitle")}
        </p>
      </div>

      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("clinicalSections.recommendedDoctors.loadingDoctors")}
          </div>
        ) : groupIds.length === 0 || doctors.length === 0 ? (
          <p className="text-sm text-gray-400 py-4">
            {t("clinicalSections.recommendedDoctors.noDoctors")}
          </p>
        ) : (
          <Select value={selectedDoctor} onValueChange={onDoctorChange}>
            <SelectTrigger className="h-12 w-full cursor-pointer">
              <SelectValue placeholder={t("clinicalSections.recommendedDoctors.selectDoctor")} />
            </SelectTrigger>

            <SelectContent
              position="popper"
              side="bottom"
              align="start"
              sideOffset={4}
              className="z-50"
            >
              {" "}
              {doctors.map((doctor) => (
                <SelectItem key={doctor.id} value={doctor.id}>
                  {doctor.name}
                  {doctor.role ? ` (${doctor.role})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
};
